/**
 * recordingVault — cofre LOCAL (IndexedDB) dos pedaços de gravação que o
 * servidor ainda não confirmou.
 *
 * POR QUE EXISTE (Sandra Vieira Ávila, 31/08/2026 — segunda consulta perdida
 * assim; a primeira foi a Magda em 28/04):
 * o áudio sobe em pedaços de 15s pro Storage, e quando a REDE cai os pedaços
 * ficam numa fila de re-tentativa que é memória da aba. A Dra. vê o timer
 * rodando (o gravador local segue funcionando), mas nada está chegando ao
 * servidor — e um F5 descarta a memória. Da consulta de 90min sobraram 1min45:
 * exatamente os 7 pedaços que a rede deixou passar antes de morrer.
 *
 * O DESENHO É DO VALDAIR, e é melhor que espelhar tudo: o cofre guarda SÓ o
 * que o servidor ainda não confirmou. Pedaço confirmado é apagado NA HORA.
 * Com internet boa o cofre vive vazio (cada pedaço passa ~1s por ele); quando
 * a rede cai, ele acumula exatamente o que falta — nem um byte a mais. Nada de
 * "gigas e gigas" no navegador.
 *
 * A ORDEM QUE NÃO PODE INVERTER: grava no cofre → tenta subir → SERVIDOR
 * CONFIRMOU → apaga. Apagar na tentativa (e não na confirmação) recriaria o
 * bug que este arquivo existe para matar.
 *
 * NUNCA no caminho crítico: toda função aqui é best-effort com catch calado.
 * Se o IndexedDB não existir (modo anônimo, quota, browser antigo), o gravador
 * se comporta EXATAMENTE como antes deste arquivo — que é o estado que grava
 * a maioria das consultas sem incidente.
 */

const DB_NAME = 'clinica-recording-vault';
const DB_VERSION = 1;
const CHUNKS = 'chunks'; // key: [sessionId, index] — o áudio pendente
const SESSIONS = 'sessions'; // key: sessionId — meta pra recuperação órfã

/** Pedaço órfão mais velho que isto é fóssil: descarta em vez de subir. */
export const VAULT_MAX_AGE_DAYS = 14;

export interface VaultSessionMeta {
  sessionId: string;
  patientId: string;
  patientName: string;
  source: 'portal' | 'nurse_note';
  storagePrefix: string;
  startTimeIso: string;
  /** Atualizado a cada pedaço gravado — distingue órfã de gravação viva. */
  lastWriteIso: string;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(CHUNKS)) {
          db.createObjectStore(CHUNKS, { keyPath: ['sessionId', 'index'] });
        }
        if (!db.objectStoreNames.contains(SESSIONS)) {
          db.createObjectStore(SESSIONS, { keyPath: 'sessionId' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Roda uma transação best-effort. Erro nunca sobe pro chamador. */
async function tx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => Promise<T> | T,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const t = db.transaction(stores, mode);
    const out = await fn(t);
    await new Promise<void>((res, rej) => {
      t.oncomplete = () => res();
      t.onerror = () => rej(t.error);
      t.onabort = () => rej(t.error);
    });
    return out;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

function reqDone(r: IDBRequest): Promise<void> {
  return new Promise((res, rej) => {
    r.onsuccess = () => res();
    r.onerror = () => rej(r.error);
  });
}

function reqValue<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

/** Registra/atualiza a meta da sessão (chamado no start e a cada pedaço). */
export async function vaultSaveMeta(meta: VaultSessionMeta): Promise<void> {
  await tx([SESSIONS], 'readwrite', (t) => reqDone(t.objectStore(SESSIONS).put(meta)));
}

/** Guarda um pedaço AINDA NÃO confirmado pelo servidor. */
export async function vaultPutChunk(sessionId: string, index: number, blob: Blob): Promise<void> {
  await tx([CHUNKS], 'readwrite', (t) =>
    reqDone(t.objectStore(CHUNKS).put({ sessionId, index, blob, savedAt: Date.now() })),
  );
}

/** O servidor confirmou este pedaço → sai do cofre. */
export async function vaultDeleteChunk(sessionId: string, index: number): Promise<void> {
  await tx([CHUNKS], 'readwrite', (t) => reqDone(t.objectStore(CHUNKS).delete([sessionId, index])));
}

/** Fim de sessão bem-sucedido (ou fóssil): remove meta e todos os pedaços. */
export async function vaultDropSession(sessionId: string): Promise<void> {
  await tx([CHUNKS, SESSIONS], 'readwrite', async (t) => {
    await reqDone(t.objectStore(SESSIONS).delete(sessionId));
    const range = IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]);
    await reqDone(t.objectStore(CHUNKS).delete(range));
  });
}

/** Pedaços pendentes de uma sessão, em ordem. */
export async function vaultGetChunks(
  sessionId: string,
): Promise<{ index: number; blob: Blob; savedAt: number }[]> {
  const rows = await tx([CHUNKS], 'readonly', (t) => {
    const range = IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]);
    return reqValue(t.objectStore(CHUNKS).getAll(range));
  });
  return ((rows as any[]) || []).sort((a, b) => a.index - b.index);
}

/** Todas as sessões que o cofre conhece (a varredura filtra viva/órfã/fóssil). */
export async function vaultListSessions(): Promise<VaultSessionMeta[]> {
  const rows = await tx([SESSIONS], 'readonly', (t) => reqValue(t.objectStore(SESSIONS).getAll()));
  return (rows as VaultSessionMeta[]) || [];
}

/** Quantos pedaços pendentes uma sessão tem (0 = nada a recuperar). */
export async function vaultCountChunks(sessionId: string): Promise<number> {
  const n = await tx([CHUNKS], 'readonly', (t) => {
    const range = IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]);
    return reqValue(t.objectStore(CHUNKS).count(range));
  });
  return typeof n === 'number' ? n : 0;
}
